/**
 * Reseller-side mock data: reseller's own businesses, staff, revenue, payouts.
 */

export type ResellerCustomer = {
  id: string;
  name: string;
  ownerEmail: string;
  plan: string;
  status: 'active' | 'trial' | 'past_due' | 'suspended' | 'canceled';
  joinedAt: string;
  mrrMinor: number;
  isPastDue: boolean;
  /** DB-mode ('shared' | 'managed' | 'self_hosted' | 'legacy') — CustomerDetail "Database" field. */
  dbMode?: string;
  /** Next-renewal date (subscription current period end) — null until a sub exists. */
  currentPeriodEnd?: string | null;
  /** Real per-period billed amount (minor units). The Subscription tab shows this + `interval`
   *  (e.g. "৳3,000 / year"), not the monthly-normalized `mrrMinor`. Null until a sub exists. */
  unitAmountMinor?: number | null;
  /** Real billing interval ('month' | 'year' | 'quarter'). Null until a sub exists. */
  interval?: string | null;
  /** The subscription's OWN status (billing state) — distinct from the account `status`.
   *  A business can be active while its sub is past_due/expired/canceled. */
  subStatus?: string | null;
};

export const resellerCustomers: ResellerCustomer[] = [
  { id: 'cu1', name: 'Greenfields Mart', ownerEmail: 'manager@greenfields.bd', plan: 'Pro', status: 'trial', joinedAt: '2026-05-03', mrrMinor: 150000, isPastDue: false, unitAmountMinor: 150000, interval: 'month', subStatus: 'trial' },
  // Account active, but the subscription's annual renewal went unpaid → sub is past_due.
  { id: 'cu2', name: 'Sky Pharmacy', ownerEmail: 'sky@sky-pharma.bd', plan: 'Basic', status: 'active', joinedAt: '2026-04-30', mrrMinor: 50000, isPastDue: true, unitAmountMinor: 50000, interval: 'month', subStatus: 'past_due' },
  { id: 'cu3', name: 'Khan Bakery', ownerEmail: 'imran@khan-bakery.com', plan: 'Starter', status: 'past_due', joinedAt: '2026-04-25', mrrMinor: 30000, isPastDue: true, unitAmountMinor: 30000, interval: 'month', subStatus: 'past_due' },
  // Yearly plan: bills ৳6,000/year (MRR ৳500/mo) — Subscription tab must show "/ year", not "/ month".
  { id: 'cu4', name: 'Riverside Diner', ownerEmail: 'owner@riverside.bd', plan: 'Basic', status: 'active', joinedAt: '2026-04-12', mrrMinor: 50000, isPastDue: false, unitAmountMinor: 600000, interval: 'year', subStatus: 'active' },
  { id: 'cu5', name: 'BookCorner', ownerEmail: 'hello@bookcorner.com', plan: 'Pro', status: 'active', joinedAt: '2026-03-22', mrrMinor: 150000, isPastDue: false, unitAmountMinor: 150000, interval: 'month', subStatus: 'active' },
  { id: 'cu6', name: 'Padma Mart', ownerEmail: 'admin@padmamart.com', plan: 'Pro', status: 'active', joinedAt: '2026-02-08', mrrMinor: 150000, isPastDue: false, unitAmountMinor: 150000, interval: 'month', subStatus: 'active' },
];

export type ResellerStaff = {
  id: string;
  email: string;
  fullName: string;
  role: 'admin' | 'staff' | 'readonly';
  status: 'active' | 'invited' | 'disabled';
  lastLogin: string | null;
};

export const resellerStaff: ResellerStaff[] = [
  { id: 'rs1', email: 'rakib@acme-resellers.com', fullName: 'Rakib Ahmed', role: 'admin', status: 'active', lastLogin: 'today 14:01' },
  { id: 'rs2', email: 'samira@acme-resellers.com', fullName: 'Samira Khatun', role: 'staff', status: 'active', lastLogin: 'yesterday' },
  { id: 'rs3', email: 'nayem@acme-resellers.com', fullName: 'Nayem Hossain', role: 'staff', status: 'invited', lastLogin: null },
  { id: 'rs4', email: 'obsolete@acme-resellers.com', fullName: 'Former Staff', role: 'staff', status: 'disabled', lastLogin: '12 Mar' },
];

/**
 * Application/membership state for a hypothetical "current logged-in user" who
 * is following the reseller path. The frontend mocks consume this to decide
 * which page or banner to show in the reseller shell.
 *
 * Set `view` to one of:
 *   'active'              — fully active reseller (default for most reseller pages)
 *   'pending_setup'       — approved, invite accepted, onboarding wizard not done
 *   'application_pending' — application awaits admin review
 *   'application_in_review'      — admin opened it
 *   'application_more_info'      — admin asked for more info
 *   'application_rejected'       — admin declined; cooldown active
 *   'application_withdrawn'      — applicant pulled it
 */
export type ResellerViewState =
  | 'active'
  | 'pending_setup'
  | 'application_pending'
  | 'application_in_review'
  | 'application_more_info'
  | 'application_rejected'
  | 'application_withdrawn';

export const currentApplicantContext = {
  view: 'active' as ResellerViewState,
  applicationId: null as string | null,
};

/** Fake "active reseller" identity for UI mocking. */
export const currentReseller = {
  id: 'rsl_1',
  name: 'Acme Resellers',
  slug: 'acme',
  commissionPct: 20,
  displayName: 'Acme POS',
  primaryColor: '#0F766E',
  logoUrl: null,
  supportEmail: 'help@acme-resellers.com',
  supportPhone: '+8801XXXXXXX',
  pricingMode: 'commission' as const,
  allowedDbModes: ['shared', 'managed', 'self_hosted'] as string[],
  domains: [
    { hostname: 'acme.itcitysoft.com', isPrimary: true, isVerified: true, tlsStatus: 'issued' as const },
    { hostname: 'acme-resellers.com', isPrimary: false, isVerified: true, tlsStatus: 'issued' as const },
  ],
  payout: {
    bankName: 'Eastern Bank Ltd.',
    accountName: 'Acme Resellers Ltd.',
    accountNumber: '1234567890',
    routingNumber: '095260340',
    bkash: '+8801XXXXXXX',
  } as { bankName?: string; accountName?: string; accountNumber?: string; routingNumber?: string; bkash?: string } | null,
  payoutVerified: true,
  notifPrefs: {} as Record<string, boolean>,
};

/**
 * Reseller monthly revenue chart (last 12 months).
 */
export const resellerRevenue = {
  periodLabel: 'This year',
  grossPaidMinor: 230400000,
  platformShareMinor: 184320000,
  yourCommissionMinor: 46080000,
  paidOutMinor: 42230000,
  outstandingMinor: 3850000,
  nextPayoutAt: '2026-05-15',
  monthly: [
    { month: 'Jun 25', bdt: 2840000 },
    { month: 'Jul 25', bdt: 3120000 },
    { month: 'Aug 25', bdt: 3580000 },
    { month: 'Sep 25', bdt: 3810000 },
    { month: 'Oct 25', bdt: 4150000 },
    { month: 'Nov 25', bdt: 3960000 },
    { month: 'Dec 25', bdt: 4210000 },
    { month: 'Jan 26', bdt: 3880000 },
    { month: 'Feb 26', bdt: 4120000 },
    { month: 'Mar 26', bdt: 4180000 },
    { month: 'Apr 26', bdt: 3820000 },
    { month: 'May 26', bdt: 4410000 },
  ],
  // method + status mirror the real reseller_payouts columns — the page renders them now
  // instead of hardcoding "Bank transfer" / "Paid" on every row.
  payouts: [
    { id: 'po1', date: '2026-04-15', bdt: 3820000, bankRef: 'XYZ123', method: 'bank_transfer', status: 'paid' },
    { id: 'po2', date: '2026-03-15', bdt: 4180000, bankRef: 'XYZ045', method: 'bkash', status: 'paid' },
    { id: 'po3', date: '2026-02-15', bdt: 3960000, bankRef: '', method: 'bkash', status: 'pending' },
  ],
};

export const resellerKpis = {
  customers: 128,
  trials: 9,
  pastDue: 5,
  mrrMinor: 19240000,
  churnPct: 3.2,
};
